import * as Popover from '@radix-ui/react-popover'
import { CalendarBlank, CaretDown, CaretLeft, CaretRight, Check, X } from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { presetRange, type DateRange } from '@/domain/dates'
import { useT } from '@/i18n'
import { cn } from './primitives'

/**
 * The period picker, like the Aliens Dashboard's: quick choices on the side, two months of calendar (one on a
 * phone), pick the first and last day (the range lights up as you hover), then Apply. Nothing reaches the page
 * until Apply, so the reader sees the period before every chart reloads. Weeks start on Saturday (Egypt).
 */

const DAY = 86_400_000
const toDay = (d: Date) => d.toISOString().slice(0, 10)
const parse = (s: string) => new Date(`${s}T00:00:00Z`)
const add = (s: string, n: number) => toDay(new Date(parse(s).getTime() + n * DAY))
const monthStart = (s: string) => `${s.slice(0, 7)}-01`
const addMonths = (s: string, n: number) => {
  const d = parse(monthStart(s))
  d.setUTCMonth(d.getUTCMonth() + n)
  return toDay(d)
}
const daysBetween = (a: string, b: string) => Math.round((parse(b).getTime() - parse(a).getTime()) / DAY) + 1

export type QuickKey = 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth' | 'last3Months' | 'thisYear' | 'lastYear'
function quickRange(k: QuickKey): DateRange {
  const today = presetRange('today').from
  switch (k) {
    case 'lastMonth': {
      const start = addMonths(today, -1)
      return { from: start, to: add(monthStart(today), -1) }
    }
    case 'thisYear':
      return { from: `${today.slice(0, 4)}-01-01`, to: today }
    case 'lastYear':
      return yearRange(Number(today.slice(0, 4)) - 1, today)
    default:
      return presetRange(k)
  }
}
/** A whole calendar year, up to today when it is this one. */
export function yearRange(year: number, today: string): DateRange {
  const to = `${year}-12-31`
  return { from: `${year}-01-01`, to: to > today ? today : to }
}
const QUICK: { key: QuickKey; ar: string; en: string }[] = [
  { key: 'today', ar: 'اليوم', en: 'Today' },
  { key: 'yesterday', ar: 'أمس', en: 'Yesterday' },
  { key: 'last7', ar: 'آخر 7 أيام', en: 'Last 7 days' },
  { key: 'last30', ar: 'آخر 30 يوما', en: 'Last 30 days' },
  { key: 'thisMonth', ar: 'هذا الشهر', en: 'This month' },
  { key: 'lastMonth', ar: 'الشهر الماضي', en: 'Last month' },
  { key: 'last3Months', ar: 'آخر 3 أشهر', en: 'Last 3 months' },
  { key: 'thisYear', ar: 'هذا العام', en: 'This year' },
  { key: 'lastYear', ar: 'العام الماضي', en: 'Last year' },
]

function Month({ month, from, to, hover, today, locale, onPick, onHover, dir }: { month: string; from: string | null; to: string | null; hover: string | null; today: string; locale: string; onPick: (d: string) => void; onHover: (d: string | null) => void; dir: number }) {
  const first = parse(month)
  const title = first.toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })
  const offset = (first.getUTCDay() + 1) % 7 // Saturday = 0
  const count = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate()
  const names = locale === 'ar' ? ['س', 'ح', 'ن', 'ث', 'ر', 'خ', 'ج'] : ['Sa', 'Su', 'Mo', 'Tu', 'We', 'Th', 'Fr']
  const end = to ?? (from && hover && hover >= from ? hover : null)
  const cells: (string | null)[] = [...Array(offset).fill(null), ...Array.from({ length: count }, (_, i) => add(month, i))]
  return (
    <div key={month} className={cn('w-[252px]', dir > 0 ? 'cal-in-next' : dir < 0 ? 'cal-in-prev' : '')}>
      <p className="mb-2 text-center text-sm font-bold text-fg-strong">{title}</p>
      <div className="grid grid-cols-7 gap-y-1 text-center">
        {names.map((n) => <span key={n} className="pb-1 text-2xs font-semibold text-fg-subtle">{n}</span>)}
        {cells.map((d, i) => {
          if (!d) return <span key={`x${i}`} />
          const isStart = d === from
          const isEnd = d === end
          const inRange = from && end && d > from && d < end
          const future = d > today
          return (
            <button
              key={d}
              type="button"
              disabled={future}
              onClick={() => onPick(d)}
              onMouseEnter={() => onHover(d)}
              className={cn(
                'cal-day relative mx-auto flex size-9 items-center justify-center text-[13px] tabular transition-colors',
                inRange && 'bg-accent-soft text-fg-strong',
                (isStart || isEnd) && 'cal-edge z-10 rounded-xl bg-accent font-bold text-white shadow-md',
                !isStart && !isEnd && !inRange && 'rounded-xl hover:bg-surface-hover',
                isStart && end && end !== from && 'rounded-e-none',
                isEnd && from && end !== from && 'rounded-s-none',
                d === today && !isStart && !isEnd && 'font-bold text-accent ring-1 ring-inset ring-accent/40',
                future && 'cursor-not-allowed opacity-30',
              )}
            >
              {Number(d.slice(8))}
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface CoreProps {
  /** null = no period at all; only reachable when `clearable` is on. */
  value: DateRange | null
  onChange: (r: DateRange | null) => void
  className?: string
  /** allows the empty period: a "مسح التاريخ" choice, an x on the trigger, and "كل التواريخ" on it when empty */
  clearable?: boolean
  /** what the trigger says when there is no period */
  placeholder?: string
  disabled?: boolean
  ariaLabel?: string
  /** only these quick choices, in this order (a phone screen needs fewer) */
  quick?: QuickKey[]
  /** the trigger as a pill that always says the choice and the days: «آخر 30 يوما · 1 أكتوبر – 30 أكتوبر» */
  pill?: boolean
}

function RangePickerCore({ value, onChange, className, clearable = false, placeholder, disabled, ariaLabel, quick, pill = false }: CoreProps) {
  const { locale } = useT()
  const ar = locale === 'ar'
  const today = presetRange('today').from
  const [open, setOpen] = useState(false)
  const [from, setFrom] = useState<string | null>(value?.from ?? null)
  const [to, setTo] = useState<string | null>(value?.to ?? null)
  const [hover, setHover] = useState<string | null>(null)
  const [view, setView] = useState(() => addMonths(value?.to || today, -1))
  const [dir, setDir] = useState(0)

  const empty = ar ? 'كل التواريخ' : 'All dates'
  const choices = quick ? quick.map((k) => QUICK.find((q) => q.key === k)!).filter(Boolean) : QUICK
  const activeQuick = value && choices.find((q) => { const r = quickRange(q.key); return r.from === value.from && r.to === value.to })
  const pendingQuick = choices.find((q) => { const r = quickRange(q.key); return r.from === from && r.to === to })
  const fmt = (s: string, withYear = true) => parse(s).toLocaleDateString(ar ? 'ar-EG' : 'en-GB', { day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' })
  const label = useMemo(() => {
    if (!value) return placeholder || empty
    if (pill) {
      const day = (s: string) => parse(s).toLocaleDateString(ar ? 'ar-EG' : 'en-GB', { day: 'numeric', month: 'long', timeZone: 'UTC' })
      const span = value.from === value.to ? day(value.from) : `${day(value.from)} – ${day(value.to)}`
      return activeQuick ? `${ar ? activeQuick.ar : activeQuick.en} · ${span}` : span
    }
    return value.from === value.to ? fmt(value.from) : `${fmt(value.from, value.from.slice(0, 4) !== value.to.slice(0, 4))} - ${fmt(value.to)}`
  }, [value, ar, placeholder, pill]) // eslint-disable-line react-hooks/exhaustive-deps

  const openChange = (o: boolean) => {
    if (o) {
      setFrom(value?.from ?? null)
      setTo(value?.to ?? null)
      setView(addMonths(value?.to || today, -1))
      setDir(0)
    }
    setOpen(o)
  }
  const clear = () => {
    setFrom(null)
    setTo(null)
    setHover(null)
    onChange(null)
    setOpen(false)
  }
  const pick = (d: string) => {
    if (!from || to) { setFrom(d); setTo(null); return }
    if (d < from) { setTo(from); setFrom(d) } else setTo(d)
  }
  const choose = (k: QuickKey) => {
    const r = quickRange(k)
    setFrom(r.from)
    setTo(r.to)
    setDir(r.to > addMonths(view, 1) ? 1 : -1)
    setView(addMonths(r.to, -1))
  }
  const nav = (n: number) => { setDir(n); setView((v) => addMonths(v, n)) }
  // a past year in one tap, and its months a jump away (owner, 2026-10-26: «كالندر التحليلات … اقدر احدد السنين ال فاتت»)
  const thisYear = Number(today.slice(0, 4))
  const years = [thisYear, thisYear - 1, thisYear - 2, thisYear - 3]
  const chooseYear = (y: number) => {
    const r = yearRange(y, today)
    setFrom(r.from)
    setTo(r.to)
    setDir(-1)
    setView(addMonths(r.to, -1))
  }
  const apply = () => {
    if (!from) return
    onChange({ from, to: to ?? from })
    setOpen(false)
  }
  const Prev = ar ? CaretRight : CaretLeft
  const Next = ar ? CaretLeft : CaretRight

  return (
    <Popover.Root open={open} onOpenChange={openChange}>
      <div className={cn('relative inline-flex max-w-full items-center', clearable && 'w-full', className)}>
        <Popover.Trigger asChild>
          <button type="button" disabled={disabled} aria-label={ariaLabel} className={cn('group inline-flex max-w-full items-center gap-2 border', pill ? 'h-11 rounded-full px-4' : 'h-10 rounded-xl px-3', 'border-border bg-surface text-sm shadow-xs transition hover:border-border-strong hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-border disabled:hover:shadow-xs data-[state=open]:border-accent data-[state=open]:ring-2 data-[state=open]:ring-accent/15', clearable && 'w-full justify-start', clearable && value && 'pe-11')}>
            <CalendarBlank size={17} weight="duotone" className="keep-color shrink-0 text-accent transition-transform group-hover:scale-110" />
            <span className={cn('truncate font-medium', value ? 'text-fg-strong' : 'text-fg-muted')}>{label}</span>
            {activeQuick && !pill && <span className="hidden shrink-0 rounded-md bg-accent-soft px-1.5 py-0.5 text-2xs font-semibold text-accent sm:inline">{ar ? activeQuick.ar : activeQuick.en}</span>}
            <CaretDown size={12} className={cn('shrink-0 text-fg-subtle transition-transform group-data-[state=open]:rotate-180', clearable && 'ms-auto')} />
          </button>
        </Popover.Trigger>
        {clearable && value && !disabled && (
          <button
            type="button"
            onClick={clear}
            aria-label={ar ? 'مسح التاريخ' : 'Clear the date'}
            title={ar ? 'مسح التاريخ' : 'Clear the date'}
            className="absolute end-1.5 flex size-8 items-center justify-center rounded-lg text-fg-subtle transition hover:bg-surface-hover hover:text-danger"
          >
            <X size={13} weight="bold" />
          </button>
        )}
      </div>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={8} collisionPadding={12} className="z-50 max-w-[calc(100vw-24px)] overflow-hidden rounded-2xl border border-border bg-surface-raised shadow-xl data-[state=open]:animate-scale-in">
          <div className="flex flex-col sm:flex-row">
            <ul className="flex gap-1 overflow-x-auto border-b border-border bg-surface p-2 [scrollbar-width:none] sm:w-40 sm:flex-col sm:overflow-visible sm:border-b-0 sm:border-e">
              {choices.map((q, i) => (
                <li key={q.key} className="shrink-0" style={{ animation: `slide-up 260ms cubic-bezier(0.16,1,0.3,1) ${i * 25}ms both` }}>
                  <button type="button" onClick={() => choose(q.key)} className={cn('flex w-full items-center justify-between gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-start text-sm transition-all', pendingQuick?.key === q.key ? 'bg-accent font-semibold text-white shadow-sm' : 'text-fg-muted hover:bg-surface-hover hover:text-fg sm:hover:ps-4')}>
                    {ar ? q.ar : q.en}
                    {pendingQuick?.key === q.key && <Check size={13} weight="bold" className="hidden sm:block" />}
                  </button>
                </li>
              ))}
              {clearable && (
                <li className="shrink-0" style={{ animation: `slide-up 260ms cubic-bezier(0.16,1,0.3,1) ${choices.length * 25}ms both` }}>
                  <button type="button" onClick={clear} className={cn('flex w-full items-center justify-between gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-start text-sm transition-all sm:mt-1 sm:border-t sm:border-border sm:pt-2', !value ? 'font-semibold text-accent' : 'text-fg-muted hover:bg-surface-hover hover:text-fg')}>
                    {empty}
                    {!value && <Check size={13} weight="bold" className="hidden sm:block" />}
                  </button>
                </li>
              )}
            </ul>
            <div className="p-3">
              <div className={cn('mb-3 flex-wrap items-center gap-1.5', quick ? 'hidden' : 'flex')}>
                <button type="button" onClick={() => nav(-12)} className="rounded-lg px-2 py-1 text-xs text-fg-muted hover:bg-surface-hover" aria-label={ar ? 'السنة السابقة' : 'Back a year'}>{ar ? '« سنة' : '« year'}</button>
                {years.map((y) => {
                  const r = yearRange(y, today)
                  const on = from === r.from && to === r.to
                  return <button key={y} type="button" onClick={() => chooseYear(y)} className={cn('rounded-lg px-2.5 py-1 text-xs font-semibold tabular transition', on ? 'bg-accent text-white shadow-sm' : 'bg-bg-subtle text-fg-muted hover:bg-surface-hover hover:text-fg')}>{y}</button>
                })}
                <button type="button" onClick={() => nav(12)} className="rounded-lg px-2 py-1 text-xs text-fg-muted hover:bg-surface-hover" aria-label={ar ? 'السنة التالية' : 'Forward a year'}>{ar ? 'سنة »' : 'year »'}</button>
              </div>
              <div className="relative flex items-start gap-5 overflow-hidden" onMouseLeave={() => setHover(null)}>
                <button type="button" onClick={() => nav(-1)} className="absolute start-0 top-0 z-10 rounded-lg p-1.5 text-fg-muted hover:bg-surface-hover" aria-label={ar ? 'الشهر السابق' : 'Previous month'}><Prev size={16} /></button>
                <button type="button" onClick={() => nav(1)} className="absolute end-0 top-0 z-10 rounded-lg p-1.5 text-fg-muted hover:bg-surface-hover" aria-label={ar ? 'الشهر التالي' : 'Next month'}><Next size={16} /></button>
                <Month key={`a${view}`} month={view} from={from} to={to} hover={hover} today={today} locale={locale} onPick={pick} onHover={setHover} dir={dir} />
                <div className="hidden sm:block"><Month key={`b${view}`} month={addMonths(view, 1)} from={from} to={to} hover={hover} today={today} locale={locale} onPick={pick} onHover={setHover} dir={dir} /></div>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                <p className="text-xs text-fg-muted">
                  {from ? (
                    <>
                      <b className="text-fg-strong">{fmt(from)}</b>
                      {to && to !== from && <> {ar ? 'إلى' : 'to'} <b className="text-fg-strong">{fmt(to)}</b></>}
                      <span className="ms-1.5 rounded-md bg-bg-subtle px-1.5 py-0.5 tabular">{daysBetween(from, to ?? from)} {ar ? 'يوم' : 'days'}</span>
                    </>
                  ) : ar ? 'اختر أول يوم' : 'Pick the first day'}
                  {from && !to && <span className="ms-1.5 text-accent">{ar ? '، اختر الآن آخر يوم' : ', now pick the last day'}</span>}
                </p>
                <div className="flex gap-2">
                  {clearable && <button type="button" onClick={clear} className="inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-fg-muted transition hover:bg-danger-soft hover:text-danger"><X size={13} weight="bold" />{ar ? 'مسح التاريخ' : 'Clear the date'}</button>}
                  <button type="button" onClick={() => setOpen(false)} className="h-9 rounded-lg px-3 text-sm text-fg-muted hover:bg-surface-hover">{ar ? 'إلغاء' : 'Cancel'}</button>
                  <button type="button" disabled={!from} onClick={apply} className="h-9 rounded-lg bg-accent px-4 text-sm font-semibold text-white shadow-sm transition active:scale-95 disabled:opacity-50">{ar ? 'تطبيق' : 'Apply'}</button>
                </div>
              </div>
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

/** The period picker everywhere: a range is always set. */
export function DateRangePicker({ value, onChange, className, quick, pill, ariaLabel }: { value: DateRange; onChange: (r: DateRange) => void; className?: string; quick?: QuickKey[]; pill?: boolean; ariaLabel?: string }) {
  return <RangePickerCore value={value} onChange={(r) => { if (r) onChange(r) }} className={className} quick={quick} pill={pill} ariaLabel={ariaLabel} />
}

/**
 * The same picker where "no period" is a real answer (the detail inquiry's three date pairs): the trigger says
 * "كل التواريخ" when it is empty, and "مسح التاريخ" (or the x on the trigger) puts it back to empty.
 */
export function OptionalDateRangePicker({ value, onChange, className, placeholder, disabled, ariaLabel }: { value: DateRange | null; onChange: (r: DateRange | null) => void; className?: string; placeholder?: string; disabled?: boolean; ariaLabel?: string }) {
  return <RangePickerCore clearable value={value} onChange={onChange} className={className} placeholder={placeholder} disabled={disabled} ariaLabel={ariaLabel} />
}
